import type { ResellerAvailableCatalogs } from 'core/services/bff';
import type { AsyncFlow, AsyncFlowWithData, STATUS } from './utilities';

export const AVAILABLE_OPTION = {
  available: 'available-only',
  unavailable: 'unavailable-only',
  first: 'available-first',
} as const;

interface ConsumerPriceWrapper {
  price: number;
  maxPrice: number;
  percent: number;
  discount: number;
}

interface ResellerPriceWrapper {
  profit: number;
  price: number;
  maxPrice?: number;
  discountPercentage?: number;
  discount?: number;
}

export interface PriceWrapper {
  consumerPrice: ConsumerPriceWrapper;
  resellerPrice: ResellerPriceWrapper;
}

export type CatalogItem = {
  brand?: string;
  category?: string;
  department?: string;
  description: string;
  image: string;
  inventory: number;
  isAvailable: boolean;
  isCustomCombo: boolean;
  hasSimilar?: boolean;
  name: string;
  onSale: boolean;
  price: Price;
  productCode: string;
  productPosition?: number;
  productOrigin?: number;
  quantity?: number;
  resellerPrice: ResellerPrice;
  resellerOnly: boolean;
  slugName: string;
  tags: string[];
  url: string;
  priceWrapper?: PriceWrapper;
  itemListName?: string;
};

type ResellerPrice = {
  price: string;
  priceNum: number;
  profit: string;
  profitNum: number;
  discountPercentage?: number;
  maxPrice?: number;
};

type PaginationBase = {
  currentPage: number;
  hasNextPage: boolean;
  itemsPerPage: number;
  nextPage: number;
  totalItems: number;
  totalPages: number;
};

type Pagination = AsyncFlowWithData<PaginationBase> & {
  isRunning: boolean;
};

type Price = {
  discount: string;
  discountNum: number;
  maxPrice: string;
  maxPriceNum: number;
  percent: string;
  percentNum: number;
  price: string;
  priceNum: number;
};

export type QueryOptions = {
  availability?: (typeof AVAILABLE_OPTION)[keyof typeof AVAILABLE_OPTION];
  category?: string;
  cycle?: number;
  department?: string;
  itemsPerPage?: number;
  key?: string;
  page?: number;
  productCode?: string;
  query?: string;
  sku?: string;
  sort?: string;
  tag?: string;
  geraCategory?: string;
  similar?: boolean;
};

export type CatalogBase = AsyncFlow & {
  data: CatalogItem[];
  previewSearch: CatalogItem[];
  isRunning: boolean;
  isRunningPreview: boolean;
  statusPreview: STATUS;
  notFound: boolean;
  pagination: Pagination;
  queryOptions: QueryOptions;
};

export type Catalog = CatalogBase & { cycleGuides: ResellerAvailableCatalogs[] };
